import * as React from "react";
import { useCallback } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";

import { Button } from "affinityUI/elements/button";
import { Card } from "affinityUI/elements/card";
import { LayoutWrapper } from "affinityUI/structures/layout";

import { isBioErrorResponse, isFetchBaseQueryError } from "../../../api/types";
import { logout } from "../../auth";

import errorAccess from "./assets/errorAccess.svg";
import errorGeneral from "./assets/errorGeneral.svg";
import errorInactive from "./assets/errorInactive.svg";
import errorNetwork from "./assets/errorNetwork.svg";
import { ErrorView } from "./errorView";

const ReloadButton: React.FC = () => {
  const handleClick = useCallback(() => {
    window.location.reload();
  }, []);

  return <Button onClick={handleClick}>Reload</Button>;
};

const LoginButton: React.FC = () => {
  const handleClick = useCallback(() => {
    logout();
  }, []);

  return <Button onClick={handleClick}>Login</Button>;
};
const ClearErrorButton: React.FC = () => {
  const { resetBoundary } = useErrorBoundary();

  const handleClick = useCallback(() => {
    resetBoundary();
  }, [resetBoundary]);

  return <Button onClick={handleClick}>Close Error</Button>;
};

const ErrorType: React.FC<{ error: Error }> = ({ error }): React.ReactNode => {
  if (error.message === "Inactivity timeout") {
    return (
      <ErrorView heading="Session timeout" error={error} imgSrc={errorInactive} actions={<LoginButton />} clearStore>
        <p>You have been logged out due to inactivity. Click reload to log back in.</p>
      </ErrorView>
    );
  }
  if (isFetchBaseQueryError(error)) {
    if (error.status === 400) {
      return (
        <ErrorView
          heading="Bad request"
          error={error}
          imgSrc={errorGeneral}
          actions={
            <>
              <ReloadButton /> <ClearErrorButton />
            </>
          }
        >
          Unexpected request has been sent to server
        </ErrorView>
      );
    }
    if (error.status === 500) {
      return (
        <ErrorView heading="Server error" error={error} imgSrc={errorGeneral} actions={<ReloadButton />}>
          <p>
            Something went wrong with our server
            <br />
            Please try again in a few moments
          </p>
        </ErrorView>
      );
    }

    if (isBioErrorResponse(error.data)) {
      if (error.data.code === "ACCESS_DENIED" || error.status === 403) {
        return (
          <ErrorView heading="Access denied" error={error} imgSrc={errorAccess}>
            You do not have access to this page
          </ErrorView>
        );
      }
      if (error.data.code === "TOKEN_NOT_VALID" || error.status === 401) {
        return (
          <ErrorView
            heading="Session timeout"
            error={error}
            imgSrc={errorInactive}
            actions={<LoginButton />}
            clearStore
          >
            <p>You have been logged out due to inactivity. Click reload to log back in.</p>
          </ErrorView>
        );
      }
    }
    if (error.status === "FETCH_ERROR") {
      return (
        <ErrorView heading="No internet connection" error={error} imgSrc={errorNetwork} actions={<ReloadButton />}>
          <p>
            Slow or no internet connection
            <br />
            Please check your internet setting
          </p>
        </ErrorView>
      );
    }
  }
  return (
    <ErrorView heading="Something wrong" error={error} imgSrc={errorGeneral} actions={<ReloadButton />}>
      {error.message}
    </ErrorView>
  );
};

export const ErrorComponent: React.FC<{ error: Error; resetErrorBoundary: (...args: Array<unknown>) => void }> = ({
  error,
}) => {
  return (
    <LayoutWrapper>
      <Card>
        <ErrorType error={error} />
      </Card>
    </LayoutWrapper>
  );
};

export const ErrorHandler: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary FallbackComponent={ErrorComponent} resetKeys={[location.key]}>
      {children}
    </ErrorBoundary>
  );
};
